/**
 * Fishing Center Panel
 * Core fishing action area: zone info, fishing button, inventory indicator,
 * quick sell, buddy activity, and catch reveal overlay.
 * Bait selection and scavenging are in BaitPanel (right column).
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { FishButton } from './FishButton';
import { FishingProgress } from './FishingProgress';
import { FishingEventBanner } from './FishingEventBanner';
import { InventoryIndicator } from './InventoryIndicator';
import { QuickSellButton } from './QuickSellButton';
import { BuddyActivityIndicator } from './BuddyActivityIndicator';
import { CatchRevealOverlay, CatchRevealData } from './CatchRevealOverlay';
import { useCurrentZone, useGameStore } from '@/state';
import { getZoneDefinition, QualityMultipliers } from '@/data';
import { GameEvent, FishInstance, FishDefinition, EquipmentSlot } from '@/types';
import { eventBus } from '@/services/EventBus';
import { EQUIPMENT_DATA } from '@/data';
import { NewPlayerWelcomePanel } from '../quests/NewPlayerWelcomePanel';
import { SessionGoalsPanel } from '@/components/goals';

interface FishingCenterPanelProps {
  onNavigateToMarket: (marketId: string) => void;
}

export function FishingCenterPanel({ onNavigateToMarket }: FishingCenterPanelProps) {
  const currentZone = useCurrentZone();
  const zone = getZoneDefinition(currentZone);

  const [catchReveal, setCatchReveal] = useState<CatchRevealData | null>(null);
  const streakRef = useRef(0);
  const lastCatchTimeRef = useRef(0);

  // Detect new records by comparing against discoveredFish BEFORE trackFishDiscovery updates
  // We snapshot the record state when the event fires; trackFishDiscovery is called first in
  // FishingEngine.processCatchResult, but the event listener fires synchronously after that,
  // so we compare the fish against the UPDATED record — if the updated record matches the fish
  // stats, it was a new record.
  const checkIsNewRecord = useCallback((fish: FishInstance): boolean => {
    const discoveries = useGameStore.getState().playerState.discoveredFish;
    const discovery = discoveries.find(d => d.definitionId === fish.definitionId);
    if (!discovery) return true; // First catch of species is always a "record"
    // If the records match this fish's stats, this fish likely set the record.
    // Use AND to avoid false positives from ties on a single dimension.
    return (
      discovery.records.maxLength === fish.length &&
      discovery.records.maxWeight === fish.weight
    );
  }, []);

  useEffect(() => {
    const unsub = eventBus.on(
      GameEvent.FISH_CAUGHT,
      ({ fish, definition }: { fish: FishInstance; definition: FishDefinition }) => {
        // Skip CatchRevealOverlay when fly rod is equipped — FlyCastOverlay handles display
        const state = useGameStore.getState();
        const rodId = state.playerState.equippedGear[EquipmentSlot.ROD];
        if (rodId) {
          const rodDef = EQUIPMENT_DATA[rodId];
          if (rodDef?.isFlyRod) return;
        }

        const now = Date.now();
        // Streak: consecutive catches within 8 seconds
        if (now - lastCatchTimeRef.current < 8000) {
          streakRef.current += 1;
        } else {
          streakRef.current = 1;
        }
        lastCatchTimeRef.current = now;

        const isNewRecord = checkIsNewRecord(fish);

        // Calculate gold value (base * quality * player bonus)
        const qualityMult = QualityMultipliers[fish.quality];
        const playerMult = 1 + state.playerStats.valueBonus;
        const goldValue = definition.baseValue * qualityMult * playerMult;

        setCatchReveal({
          fish,
          definition,
          isNewRecord,
          streak: streakRef.current,
          goldValue,
        });
      },
    );

    return unsub;
  }, [checkIsNewRecord]);

  const handleDismiss = useCallback(() => {
    setCatchReveal(null);
  }, []);

  return (
    <div className="relative flex flex-col items-center p-6 space-y-6">
      {/* Zone Info */}
      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold text-white">{zone.name}</h2>
        <p className="text-sm text-gray-400">{zone.description}</p>
      </div>

      {/* New Player Welcome Panel */}
      <div className="w-full max-w-2xl">
        <NewPlayerWelcomePanel onNavigateToMarket={onNavigateToMarket} />
      </div>

      {/* Fishing Event Banner */}
      <FishingEventBanner />

      {/* Session Goals */}
      <SessionGoalsPanel />

      {/* Fishing Action Area */}
      <div className="flex flex-col items-center space-y-4">
        <InventoryIndicator />
        <FishButton />
        <FishingProgress />
        <QuickSellButton />
      </div>

      {/* Buddy Activity */}
      <BuddyActivityIndicator />

      {/* Catch reveal overlay — positioned over the entire fishing panel */}
      <CatchRevealOverlay data={catchReveal} onDismiss={handleDismiss} />
    </div>
  );
}
